import React, { useState } from 'react'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { Row, Col } from 'reactstrap'
import FieldsetItem from 'components/FieldsetItem'
import {
  DOWNLOAD_LESSON_REPORT_TEXT,
  PREPARING_LESSON_REPORT_TEXT,
} from './constants'
import {
  AssignmentType,
  NetSuiteAssetStatus,
  NORMALIZED_SKU_CODES,
  Site,
  SiteUser,
  SkuCode,
  Skus,
} from 'types'
import CsvDownloadLink from 'components/CsvDownloadLink'
import { IsRosteredBadge } from 'components/IsRosteredBadge/IsRosteredBadge'
import {
  DownloadLink,
  LicenseDetails,
  LicenseHeader,
  LicenseName,
  LicenseRow,
  ReportLink,
  ReportLinkWrapper,
  StyledButton,
} from './component.styles'
import SitePreferencesModal from './SitePreferencesModal'

interface SiteSummaryProps {
  accessCodes?: string[]
  handleClearSiteCache: () => void
  isFetchingReports: Record<string, boolean>
  onFetchLessonProgressReport: (siteId: number, skuCode: string) => void
  onFetchTrainingReport: (
    siteUserEmails: string[],
    skuCode: string,
    siteName: string,
  ) => void
  reports: Record<string, any>
  site: Site
  siteUsers?: SiteUser[]
}

const SiteSummary: React.FC<SiteSummaryProps> = ({
  accessCodes,
  handleClearSiteCache,
  isFetchingReports,
  onFetchLessonProgressReport,
  onFetchTrainingReport,
  reports,
  site,
  siteUsers = [],
}) => {
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false)

  const {
    createdDate,
    id,
    isRostered,
    modifiedDate,
    name,
    netSuiteAssets = [],
    netSuiteCustomer,
    organization,
    preferences = [],
  } = site
  const { nsCustomerId } = netSuiteCustomer || {}
  const { id: orgId, name: orgName } = organization || {}
  const siteUserEmails =
    siteUsers && siteUsers.map(siteUser => siteUser.inviteEmail)
  const sitePreferences = preferences.filter(
    preference => preference.assignmentType !== AssignmentType.SiteUser,
  )

  const handleReportClick = (skuCode: string) => {
    if (skuCode === Skus.CPUSkuCode || skuCode === Skus.BPUSkuCode) {
      onFetchTrainingReport(siteUserEmails, skuCode, name)
    } else {
      onFetchLessonProgressReport(id, skuCode)
    }
  }

  const normalizeSkuCode = (skuCode: string): string => {
    return NORMALIZED_SKU_CODES.includes(skuCode as SkuCode) ||
      skuCode === Skus.K8FamilySkuCode
      ? Skus.K8SkuCode
      : skuCode.toUpperCase()
  }

  const formatExpirationDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-us', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <fieldset>
      <legend>Details</legend>
      <Row>
        <Col sm={4}>
          <FieldsetItem data={nsCustomerId} title="Site NS Customer Id" />
          <FieldsetItem data={orgId} title="SecondStep Organization Id" />
          <StyledButton color="primary" onClick={handleClearSiteCache}>
            Clear Site Cache
          </StyledButton>
        </Col>
        <Col sm={5}>
          <FieldsetItem data={name} title="Site Name" />
          <FieldsetItem data={orgName} title="SecondStep Organization Name" />
          {accessCodes && accessCodes.length > 0 && (
            <FieldsetItem
              data={accessCodes.join(', ')}
              title="Family Access Codes"
            />
          )}
        </Col>
        <Col sm={3}>
          <FieldsetItem
            color="none"
            data={createdDate ? moment(createdDate).format('YYYY-MM-DD') : ''}
            title="Created Date"
          />
          <FieldsetItem
            color="none"
            data={modifiedDate ? moment(modifiedDate).format('YYYY-MM-DD') : ''}
            title="Modified Date"
          />
          <FieldsetItem color="none" title="Is Rostered">
            <IsRosteredBadge isRostered={isRostered} summaryView={true} />
          </FieldsetItem>
          <StyledButton
            color="primary"
            onClick={() => setIsPreferencesModalOpen(true)}
          >
            Display Preferences
          </StyledButton>
        </Col>
      </Row>
      <LicenseHeader>Licenses</LicenseHeader>
      {netSuiteAssets.length > 0 ? (
        netSuiteAssets.map((netSuiteAsset, index) => {
          if (
            netSuiteAsset.status === NetSuiteAssetStatus.Expired ||
            netSuiteAsset.status === NetSuiteAssetStatus.Canceled
          )
            return null

          // Handle programCodes as an array - normalize each code and deduplicate
          const programCodesArray = netSuiteAsset?.product?.programCodes || []
          const skuCodesToRender = Array.from(
            new Set(
              programCodesArray
                .filter((code): code is string => Boolean(code))
                .map(code => normalizeSkuCode(code)),
            ),
          )

          return (
            <Row index={index} key={netSuiteAsset.id}>
              <Col sm={12}>
                <LicenseRow>
                  <LicenseName>
                    <Link to={`/css-admin/licenses/${netSuiteAsset.id}`}>
                      <div>{netSuiteAsset.product.description}</div>
                      <div>{`License ID: ${netSuiteAsset.nsName}`}</div>
                    </Link>
                  </LicenseName>
                  <LicenseDetails>
                    <div>
                      Starts:{' '}
                      {formatExpirationDate(netSuiteAsset.originationDate)}
                    </div>
                    <div>
                      Expires:{' '}
                      {formatExpirationDate(netSuiteAsset.expirationDate) ||
                        formatExpirationDate(netSuiteAsset.cancelledDate)}
                    </div>
                    <div>Is Active: {netSuiteAsset.isActive ? '✅' : '❌'}</div>
                  </LicenseDetails>
                  {skuCodesToRender.map((programCodes, codeIndex) => {
                    if (!programCodes || programCodes === Skus.ADLSkuCode)
                      return null

                    const licenseReport = reports[programCodes]
                    const isFetchingReport = isFetchingReports[programCodes]
                    let data: any[] | undefined,
                      filename: string | undefined,
                      headers: string[] | undefined,
                      reportUrl: string | undefined

                    if (programCodes === Skus.K8SkuCode) {
                      reportUrl = licenseReport as string
                    }
                    if (
                      programCodes === Skus.CPUSkuCode ||
                      programCodes === Skus.BPUSkuCode
                    ) {
                      const reportData = licenseReport as {
                        data?: any[]
                        filename?: string
                        headers?: string[]
                      }
                      data = reportData?.data
                      filename = reportData?.filename
                      headers = reportData?.headers
                    }

                    const progressReportLinkText = isFetchingReport
                      ? PREPARING_LESSON_REPORT_TEXT
                      : DOWNLOAD_LESSON_REPORT_TEXT

                    return (
                      <ReportLinkWrapper
                        key={`${netSuiteAsset.id}-${codeIndex}`}
                      >
                        {(licenseReport === null || isFetchingReport) && (
                          <ReportLink
                            dataTestId={`get-lesson-progress-report-link-${programCodes}`}
                            disabled={isFetchingReport}
                            onClick={() => handleReportClick(programCodes)}
                          >
                            {progressReportLinkText}{' '}
                            {skuCodesToRender.length > 1
                              ? `(${programCodes})`
                              : ''}
                          </ReportLink>
                        )}
                        {data &&
                          data.length > 0 &&
                          (programCodes === Skus.CPUSkuCode ||
                            programCodes === Skus.BPUSkuCode) &&
                          filename &&
                          headers && (
                            <CsvDownloadLink
                              data={data}
                              filename={filename}
                              headers={headers}
                            >
                              {DOWNLOAD_LESSON_REPORT_TEXT}
                            </CsvDownloadLink>
                          )}
                        {reportUrl && programCodes === Skus.K8SkuCode && (
                          <DownloadLink href={reportUrl}>
                            {DOWNLOAD_LESSON_REPORT_TEXT}
                          </DownloadLink>
                        )}
                      </ReportLinkWrapper>
                    )
                  })}
                </LicenseRow>
              </Col>
            </Row>
          )
        })
      ) : (
        <div>Site Has No Licenses</div>
      )}
      <SitePreferencesModal
        isOpen={isPreferencesModalOpen}
        preferences={sitePreferences}
        toggle={() => setIsPreferencesModalOpen(false)}
      />
    </fieldset>
  )
}

export default SiteSummary
